import React, { memo, useMemo, useRef } from 'react'
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { TextField, Popover } from '@mui/material'
import { dateFnsLocale } from '../../../utils/localeUtils'
import { useDateTimePicker } from './useDateTimePicker'

interface DateTimePickerProps {
  id?: string
  label?: string
  value: Date | null
  onChange: (selectedDateTime: Date | null, keyboardInputValue?: string) => void
  error?: boolean
  required?: boolean
  disabled?: boolean
  shouldDisableTime?: any
  onError?: any
  minutesStep?: number
  dataTestId?: string
  customOnClose?: (fullDate: Date) => void
  maxDate?: Date
  helperText?: string
  disabledDates?: string[]
  isHTL?: boolean
  disableFuture?: boolean
}

const DateTimePicker = memo(({
                          id,
                          label,
                          value,
                          onChange,
                          error,
                          required,
                          disabled,
                          disableFuture,
                          shouldDisableTime,
                          onError,
                          minutesStep = 5,
                          dataTestId,
                          customOnClose,
                          maxDate,
                          helperText,
                          disabledDates,
                          isHTL,
                        }: DateTimePickerProps) => {
  const inputRef = useRef<HTMLDivElement>(null)

  const {
    showDatePicker,
    showTimePicker,
    selectedDate,
    displayValue,
    handleDateSelected,
    handleTimeSelected,
    openDatePicker,
    closeDatePicker,
    closeTimePicker,
    handleDayButtonClick
  } = useDateTimePicker({ value, onChange, customOnClose })

  const shouldDisableDate = (date: Date) => {
    if (!disabledDates || disabledDates.length === 0) return false
    return disabledDates.some(
      (disabledDate) => date.toISOString().split('T')[0] === new Date(disabledDate).toISOString().split('T')[0]
    )
  }

  const maxTime = useMemo(() => {
    if (!selectedDate) return undefined

    const now = new Date()
    let maxAllowedDate = maxDate

    if (disableFuture && !maxAllowedDate) {
      maxAllowedDate = now
    }
    
    if (!maxAllowedDate) {
      return undefined
    }

    const isSameDay = maxAllowedDate.toDateString() === selectedDate.toDateString()
    const isMaxDateInFuture = maxAllowedDate >= now
    
    return isSameDay && isMaxDateInFuture ? maxAllowedDate : undefined
  }, [maxDate, selectedDate, disableFuture])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <div>
        <div ref={inputRef}>
          <TextField
            id={id}
            label={label}
            value={displayValue}
            onClick={disabled ? undefined : openDatePicker}
            required={required}
            error={error}
            helperText={helperText}
            fullWidth
            disabled={disabled}
            inputProps={{
              'data-testid': dataTestId,
              readOnly: true,
            }}
          />
        </div>

        <Popover
          open={showDatePicker}
          anchorEl={inputRef.current}
          onClose={closeDatePicker}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <div
            role="presentation"
          >
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={selectedDate}
              onChange={handleDateSelected}
              maxDate={maxDate}
              disableFuture={disableFuture}
              shouldDisableDate={shouldDisableDate}
              slotProps={{
                day: {
                  onClick: (_e) => {
                    handleDayButtonClick()
                  },
                },
              }}
            />
          </div>
        </Popover>

        <TimePicker
          value={selectedDate}
          onChange={handleTimeSelected}
          ampm={false}
          minutesStep={minutesStep}
          open={showTimePicker}
          closeOnSelect
          onClose={closeTimePicker}
          maxTime={maxTime}
          slotProps={{
            popper: {
              anchorEl: inputRef.current,
              placement: 'bottom-start',
            },
            textField: {
              style: {
                position: 'absolute',
                visibility: 'hidden',
                width: 0,
                height: 0,
                overflow: 'hidden',
              },
            },
            actionBar: {
              actions: ['accept', 'cancel'],
              sx: {
                '& .MuiDialogActions-root': {
                  justifyContent: 'center',
                  paddingBottom: 2,
                },
                '& .MuiButton-root': {
                  margin: '0 auto',
                },
              },
            },
          }}
        />
      </div>
    </LocalizationProvider>
  )
})

export default DateTimePicker
